/**
 * The one definition of "an OfficeHourDTO entry that counts".
 *
 * Three call sites derive from it — the School Details week table, its
 * Schema.org `openingHours` strings, and the Location Search Results card's
 * single "today" line — and they must not drift: a rule that held in one place
 * and not another would show a visitor two different opening times for the same
 * Standort on two pages.
 */

/**
 * The subset of `OfficeHourDTO` every consumer shares. Structural rather than
 * imported from one generated DTO, because the search endpoint and the location
 * endpoint each carry their own nominally distinct type for the same shape.
 */
export interface OfficeHourEntry {
  weekday?: string;
  startTime?: string | null;
  endTime?: string | null;
  closed?: boolean | null;
  disabled?: boolean | null;
  standard?: boolean | null;
}

/** Monday-first, the order both the table and the JSON-LD grouping need. */
export const WEEKDAY_ORDER = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

/**
 * A recurring, active entry: `standard === false` marks a one-off override and
 * `disabled === true` a retired row. Both defaults are permissive, because the
 * backend omits the flags for the ordinary case.
 */
export function isUsableOfficeHour(entry: OfficeHourEntry): boolean {
  return entry.standard !== false && entry.disabled !== true;
}

/** `isUsableOfficeHour`, sorted Monday-first. Does not mutate the input. */
export function usableOfficeHours(
  officeHours: OfficeHourEntry[] | undefined,
): OfficeHourEntry[] {
  return (officeHours ?? [])
    .filter(isUsableOfficeHour)
    .sort(
      (a, b) =>
        WEEKDAY_ORDER.indexOf(a.weekday ?? '') -
        WEEKDAY_ORDER.indexOf(b.weekday ?? ''),
    );
}
